import { analyticsConfig } from "../config/analytics";

// =============================================================================
// Analytics — GA4 custom events, Google Ads conversions, UTM handling
// =============================================================================
// Consent-gated (EU ePrivacy): no Google script loads until the visitor accepts
// via the CookieConsent banner. The gtag stub in index.html queues calls into
// dataLayer, so trackEvent/trackConversion are always safe to call; with consent
// declined the queue simply never ships anywhere.
// =============================================================================

type Brand = "camdeed" | "tuokis";

// ---------------------------------------------------------------------------
// Brand detection (non-React, safe to call outside components)
// ---------------------------------------------------------------------------

const getBrandFromUrl = (): Brand => {
  const href = window.location.href;
  const isCamdeed =
    href.includes("camdeed.app") ||
    href.includes("camdeed.com") ||
    href.includes("localhost") ||
    href.includes("127.0.0.1");
  return isCamdeed ? "camdeed" : "tuokis";
};

const getBrandConfig = () => {
  const brand = getBrandFromUrl();
  return { brand, config: analyticsConfig[brand] };
};

// ---------------------------------------------------------------------------
// gtag helper — typed wrapper around the global gtag function
// ---------------------------------------------------------------------------

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gtag(...args: any[]): void;
}

// ---------------------------------------------------------------------------
// UTM parameter handling
// ---------------------------------------------------------------------------

const UTM_STORAGE_KEY = "analytics_utm_params";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

type UtmParams = Partial<Record<(typeof UTM_PARAMS)[number], string>>;

const captureUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  let hasUtm = false;

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
      hasUtm = true;
    }
  }

  // Only overwrite if the current URL has UTM params (preserve earlier capture)
  if (hasUtm) {
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    } catch {
      // localStorage unavailable (private browsing, etc.) — silently ignore
    }
  }
};

export const getUtmParams = (): UtmParams => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// ---------------------------------------------------------------------------
// Consent (Google Consent Mode v2, strictest form: nothing loads until accepted)
// ---------------------------------------------------------------------------

const CONSENT_STORAGE_KEY = "camdeed-cookie-consent-v1";

export type AnalyticsConsent = "granted" | "denied";

export const getAnalyticsConsent = (): AnalyticsConsent | null => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    return null;
  }
};

// Called by the CookieConsent banner. Granting mid-session flips Consent Mode
// and loads the tags immediately; declining just records the choice.
export const setAnalyticsConsent = (consent: AnalyticsConsent) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  } catch {
    // localStorage unavailable — the banner will show again next load
  }
  if (consent === "granted") {
    grantConsentAndLoadTags();
  }
};

// Forget the stored choice so the banner shows again (linked from /privacy).
export const resetAnalyticsConsent = () => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const grantConsentAndLoadTags = () => {
  gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  loadGoogleTags();
};

// ---------------------------------------------------------------------------
// Initialization — call once on app startup
// ---------------------------------------------------------------------------

let initialized = false;

export const initAnalytics = () => {
  if (initialized) return;
  initialized = true;

  // Consent Mode v2 defaults: everything denied before any tag could load.
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  // UTM capture is first-party localStorage only, independent of consent.
  captureUtmParams();

  if (getAnalyticsConsent() === "granted") grantConsentAndLoadTags();
};

let tagsLoaded = false;

const loadGoogleTags = () => {
  if (tagsLoaded) return;
  tagsLoaded = true;

  const { brand, config } = getBrandConfig();

  // 1. Load gtag.js dynamically with the correct brand's GA4 measurement ID
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4MeasurementId}`;
  document.head.appendChild(script);

  // 2. Configure GA4 with cross-domain tracking
  // Add ?ga_debug=true to any URL to enable DebugView — persists through redirects (e.g. Stripe)
  const hasDebugParam = new URLSearchParams(window.location.search).has("ga_debug");
  if (hasDebugParam) {
    try {
      localStorage.setItem("ga_debug", "1");
    } catch {
      /* ignore */
    }
  }
  let debugMode = hasDebugParam;
  try {
    debugMode = debugMode || localStorage.getItem("ga_debug") === "1";
  } catch {
    /* ignore */
  }

  gtag("js", new Date());
  gtag("config", config.ga4MeasurementId, {
    ...(debugMode && { debug_mode: true }),
    linker: {
      domains: config.crossDomainLinkerDomains,
    },
  });

  // 3. Load Google Ads conversion tracking (if conversion ID is configured)
  if (!config.googleAdsConversionId.includes("TODO") && !config.googleAdsConversionId.includes("_ID")) {
    gtag("config", config.googleAdsConversionId);
  }

  // eslint-disable-next-line no-console
  console.log(`[Analytics] Initialized for brand: ${brand}, GA4: ${config.ga4MeasurementId}`);
};

// ---------------------------------------------------------------------------
// Event tracking — GA4 custom events
// ---------------------------------------------------------------------------

export const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
  const { brand, config } = getBrandConfig();

  const eventParams = {
    brand,
    ...params,
    send_to: config.ga4MeasurementId,
  };

  gtag("event", eventName, eventParams);
};

// ---------------------------------------------------------------------------
// Google Ads conversion tracking
// ---------------------------------------------------------------------------

export const trackConversion = (
  conversionType: "paymentCompleted" | "registrationSubmitted",
  params?: Record<string, unknown>,
) => {
  const { config } = getBrandConfig();
  const conversionLabel = config.googleAdsConversions[conversionType]?.label;

  // Skip if conversion IDs aren't configured yet
  if (config.googleAdsConversionId.includes("TODO") || config.googleAdsConversionId.includes("_ID")) return;
  if (conversionLabel.includes("TODO") || conversionLabel.includes("_LABEL")) return;

  gtag("event", "conversion", {
    send_to: `${config.googleAdsConversionId}/${conversionLabel}`,
    ...params,
  });
};
